import { withErrorBoundary } from "@sentry/nextjs";
import { gql } from "graphql-request";
import { VFC } from "react";
import styled from "styled-components";
import { Select } from "components";
import { useFormField, useAnchor } from "hooks";
import { TableOpenRolesContent } from "types/graphql/generated";
import { DepartmentJobs } from "./components";
import { filterByDepartment, filterByLocation, filterEmptyDepartments } from "./filters";
import { useOpenRoles } from "./hooks";
import { getAvailableDepartmentNames, getAvailableLocationNames } from "./utils";

interface Props {
  content: TableOpenRolesContent;
}

const TableOpenRoles: VFC<Props> = withErrorBoundary(
  ({ content: { title, departmentsFilterPlaceholder, locationsFilterPlaceholder } }) => {
    const { departments } = useOpenRoles();

    const selectedDepartmentName = useFormField();
    const selectedLocationName = useFormField();

    const availableDepartmentNames = getAvailableDepartmentNames(departments, selectedLocationName.value);
    const availableLocationsNames = getAvailableLocationNames(departments, selectedDepartmentName.value);

    const filteredDepartments =
      departments?.length > 0
        ? departments
            .filter(filterByDepartment(selectedDepartmentName.value))
            .filter(filterByLocation(selectedLocationName.value))
            .filter(filterEmptyDepartments)
        : [];

    useAnchor("open-roles");
    const hasOpenRoles = filteredDepartments.length > 0;

    return (
      <StyledTableOpenRoles className="Content open-roles">
        <div className="TableOpenRoles__header">
          <h3 className="TableOpenRoles__title Font_40_48_600">{title}</h3>
          <div className="TableOpenRoles__filters">
            {hasOpenRoles ? (
              <>
                <Select
                  className="TableOpenRoles__filter"
                  value={selectedDepartmentName.value}
                  onChange={selectedDepartmentName.onChange}
                  id={departmentsFilterPlaceholder}
                >
                  <option value="">{departmentsFilterPlaceholder}</option>
                  {availableDepartmentNames.map((selectedDepartmentName) => (
                    <option key={selectedDepartmentName} value={selectedDepartmentName}>
                      {selectedDepartmentName}
                    </option>
                  ))}
                </Select>
                <Select
                  className="TableOpenRoles__filter"
                  value={selectedLocationName.value}
                  onChange={selectedLocationName.onChange}
                  id={locationsFilterPlaceholder}
                >
                  <option value="">{locationsFilterPlaceholder}</option>
                  {availableLocationsNames.map((selectedLocationName) => (
                    <option key={selectedLocationName} value={selectedLocationName}>
                      {selectedLocationName}
                    </option>
                  ))}
                </Select>
              </>
            ) : null}
          </div>
        </div>
        <div className="TableOpenRoles__departments">
          {hasOpenRoles
            ? filteredDepartments.map((department) => (
                <div key={department.id} className="TableOpenRoles__department">
                  <h4 className="TableOpenRoles__departmentTitle Font_24_32_600">{department.name}</h4>
                  <div className="TableOpenRoles__jobs">
                    <DepartmentJobs department={department} selectedLocationName={selectedLocationName.value} />
                  </div>
                </div>
              ))
            : null}
        </div>
      </StyledTableOpenRoles>
    );
  },
  {}
);

const StyledTableOpenRoles = styled.div`
  padding-top: 128px;
  margin-top: -128px;

  .TableOpenRoles__header {
    display: flex;
    justify-content: space-between;
  }

  .TableOpenRoles__title {
    width: 390px;
    flex-shrink: 0;
  }

  .TableOpenRoles__filters {
    flex-grow: 1;
    display: flex;
    margin-left: 10px;
  }

  .TableOpenRoles__filter {
    flex: 1;
  }

  .TableOpenRoles__filter:not(:first-child) {
    margin-left: 32px;
  }

  .TableOpenRoles__departments {
    margin-top: 36px;
  }

  .TableOpenRoles__department {
    display: flex;
    padding-top: 24px;
    padding-bottom: 24px;
  }

  .TableOpenRoles__department:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.colors.fog["30"]};
  }

  .TableOpenRoles__department > * + * {
    margin-left: 10px;
  }

  .TableOpenRoles__departmentTitle {
    flex-shrink: 0;
    width: 390px;
  }

  .TableOpenRoles__jobs {
    width: 100%;
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.lg}px`}) {
    .TableOpenRoles__title {
      width: 340px;
    }

    .TableOpenRoles__departmentTitle {
      width: 340px;
    }

    .TableOpenRoles__departments {
      margin-top: 24px;
    }

    .TableOpenRoles__department {
      padding-top: 16px;
      padding-bottom: 16px;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.md}px`}) {
    .TableOpenRoles__header {
      flex-direction: column;
    }

    .TableOpenRoles__header > * + * {
      margin-left: 0;
      margin-top: 24px;
    }

    .TableOpenRoles__department {
      flex-direction: column;
    }

    .TableOpenRoles__department > * + * {
      margin-left: 0;
      margin-top: 24px;
    }
  }

  @media (max-width: ${({ theme }) => `${theme.breakpoints.sm}px`}) {
    .TableOpenRoles__filters {
      flex-direction: column;
    }

    .TableOpenRoles__filter:not(:first-child) {
      margin-left: 0;
      margin-top: 16px;
    }

    .TableOpenRoles__department {
      padding-top: 24px;
      padding-bottom: 24px;
    }
  }
`;

gql`
  fragment TableOpenRolesContent on ComponentTableTableOpenRoles {
    title
    locationsFilterPlaceholder
    departmentsFilterPlaceholder
  }
`;

export { TableOpenRoles };
export type { Props as TableOpenRolesProps };
